/**
 * Surligne visuellement la PREMIÈRE occurrence du `source_text` de chaque clue
 * TROUVÉE dans les blocs de scope (`data-clue-scope`). Seule la première
 * occurrence est marquée (pas toutes les répétitions ensuite) : le surlignage
 * pointe l'endroit où l'indice a été révélé, pas chaque mention du texte.
 * L'occurrence trouvée devient un div absolute positionné par
 * `Range.getBoundingClientRect()`, sans bordure (fond emerald léger + ombre
 * discrète).
 *
 * `pointer-events: none` sur les overlays : les clics passent au wrapper
 * scope et sont routés par `useClueOverlay` (délégation d'événement) — donc
 * la logique métier reste dans le context.
 *
 * Recalcul via `ResizeObserver` sur le scope + listener `window.scroll` /
 * `window.resize`. Un `MutationObserver` léger sur le scope détecte les
 * changements de contenu (rendu asynchrone de KaTeX, etc.).
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useClueHuntCtx } from './ClueHuntContext';
import { normalizeClueMatch } from '@jeraisonne/db';

interface Rect {
  clueId: string;
  found: boolean;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ClueTargetsRendererProps {
  scopeRef: React.RefObject<HTMLElement | null>;
  /** Clues additionnels des chasses TERMINÉES (frozen) — on affiche les
      encadrés verts sur leurs source_text révélés même hors provider actif. */
  extraClues?: Array<{ id: string; source_text: string }>;
  /** IDs des indices révélés dans les chasses terminées (union avec ctx). */
  extraRevealed?: Set<string>;
}

export function ClueTargetsRenderer({
  scopeRef,
  extraClues,
  extraRevealed,
}: ClueTargetsRendererProps) {
  const ctx = useClueHuntCtx();
  const [rects, setRects] = useState<Rect[]>([]);
  const rafRef = useRef<number | null>(null);

  // Union des clues (chasse en cours + toutes les chasses terminées).
  const clues = [...(ctx?.data.clues ?? []), ...(extraClues ?? [])];
  const revealedIds = new Set<string>();
  if (ctx?.state.revealed) {
    for (const id of ctx.state.revealed) revealedIds.add(id);
  }
  if (extraRevealed) for (const id of extraRevealed) revealedIds.add(id);
  const revealed = revealedIds;
  const enabled = clues.length > 0;

  useLayoutEffect(() => {
    if (!enabled) {
      setRects([]);
      return;
    }
    const scope = scopeRef.current;
    if (!scope) return;

    function schedule() {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(compute);
    }

    function compute() {
      rafRef.current = null;
      const scopeEl = scopeRef.current;
      if (!scopeEl) return;
      const scopeRect = scopeEl.getBoundingClientRect();
      const out: Rect[] = [];

      for (const clue of clues) {
        // On N'AFFICHE QUE les indices déjà TROUVÉS/RÉVÉLÉS : signaler les
        // non-trouvés reviendrait à indiquer à l'élève où sont les indices
        // (triche). L'élève doit CHERCHER dans le texte. Une fois trouvé,
        // l'encadré vert marque visuellement où était l'indice.
        const found = revealed?.has(clue.id) ?? false;
        if (!found) continue;
        const needle = normalizeClueMatch(clue.source_text);
        if (!needle) continue;
        // Une seule occurrence : la PREMIÈRE (en ordre DOM). On ne surligne pas
        // toutes les répétitions du texte plus loin dans l'énoncé/l'historique.
        const foundRects = findTextRects(scopeEl, clue.source_text, 1);
        const r = foundRects[0];
        if (r) {
          out.push({
            clueId: clue.id,
            found: true,
            x: r.left - scopeRect.left,
            y: r.top - scopeRect.top,
            w: r.width,
            h: r.height,
          });
        }
      }
      setRects(out);
    }

    schedule();

    const ro = new ResizeObserver(schedule);
    ro.observe(scope);

    const mo = new MutationObserver(schedule);
    mo.observe(scope, { childList: true, subtree: true, characterData: true });

    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
    };
  }, [enabled, clues, revealed, scopeRef]);

  if (!enabled) return null;

  return (
    <>
      {rects.map((r, i) => (
        <div
          key={`${r.clueId}-${i}`}
          // Indice TROUVÉ : fond emerald léger + ombre discrète, sans bordure.
          // Les non-trouvés ne sont volontairement PAS affichés — l'élève doit
          // les chercher.
          className="pointer-events-none absolute rounded-md bg-emerald-200/30 shadow-[0_1px_5px_rgba(16,185,129,0.45)] transition-all duration-200"
          style={{
            left: r.x - 2,
            top: r.y - 2,
            width: r.w + 4,
            height: r.h + 4,
          }}
          aria-hidden
        />
      ))}
    </>
  );
}

/**
 * Trouve les rects DOM (viewport) des occurrences de `needle` dans le sous-
 * arbre `root`. Deux passes :
 *   1) Text nodes visibles (TreeWalker) — pour du texte normal.
 *   2) Annotations LaTeX (.katex annotation[encoding="application/x-tex"]) —
 *      pour matcher les source_text qui sont du LaTeX raw (ex. "6{,}9 \\times
 *      t") contre les formules rendues KaTeX. Dans ce cas, le rect renvoyé
 *      est celui du `.katex` parent (formule visible).
 *
 * Bornée à `limit` occurrences pour éviter les explosions.
 */
function findTextRects(root: HTMLElement, needle: string, limit: number): DOMRect[] {
  const norm = normalizeClueMatch(needle);
  if (!norm) return [];
  const out: DOMRect[] = [];

  // ─── Passe 1 : text nodes visibles ──────────────────────────────────────
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node: Node) {
      if (!node.textContent || !node.textContent.trim()) return NodeFilter.FILTER_REJECT;
      const el = (node as Text).parentElement;
      if (el?.closest('.katex-mathml')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  let node: Node | null = walker.currentNode;
  while ((node = walker.nextNode())) {
    if (out.length >= limit) break;
    const text = (node as Text).textContent ?? '';
    if (!normalizeClueMatch(text).includes(norm)) continue;
    const lowText = text.toLowerCase();
    const target = needle.trim().toLowerCase();
    let from = 0;
    while (from < lowText.length && out.length < limit) {
      const idx = lowText.indexOf(target, from);
      if (idx < 0) break;
      try {
        const range = document.createRange();
        range.setStart(node, idx);
        range.setEnd(node, idx + target.length);
        const rect = range.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) out.push(rect);
      } catch {
        /* offset invalide */
      }
      from = idx + target.length;
    }
  }

  // ─── Passe 2 : annotations KaTeX (LaTeX raw) ───────────────────────────
  if (out.length < limit) {
    const annotations = root.querySelectorAll<HTMLElement>(
      '.katex annotation[encoding="application/x-tex"]',
    );
    for (const anno of annotations) {
      if (out.length >= limit) break;
      const src = anno.textContent?.trim() ?? '';
      if (!src) continue;
      // Match : le source du LaTeX contient `needle` ? Comparaison insensible
      // à la casse et aux espaces (les prompts LLM peuvent différer un peu).
      const normSrc = src.replace(/\s+/g, '').toLowerCase();
      const normNeedle = needle.replace(/\s+/g, '').toLowerCase();
      if (!normSrc.includes(normNeedle)) continue;
      // Rect = celui du `.katex` parent (formule visible).
      const katexRoot = anno.closest('.katex') as HTMLElement | null;
      if (!katexRoot) continue;
      const rect = katexRoot.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) out.push(rect);
    }
  }

  return out;
}
